import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, Button, ButtonGroup, HStack, Text, Textarea, useDialogContext, VStack} from '@chakra-ui/react'
import {PatientProtocol} from '@cypro/engine/queries'
import {dialog, DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {toaster} from '@cypro/ui/toaster'
import {isSequenceDoctorValidated} from '@cypro/engine/utils'
import {getShortDateFormatter} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {useMemo, useState} from 'react'
import {StopProtocolDialog} from './stop-protocol-dialog'

export interface RemoveProtocolDialogProps {
  patientProtocol: PatientProtocol
}

export function RemoveProtocolDialog({patientProtocol}: RemoveProtocolDialogProps) {
  const {t} = useTranslate('common')
  const context = useDialogContext()
  const zero = useZero()
  const [isRemoving, setIsRemoving] = useState(false)
  const [reason, setReason] = useState('')
  const [patientTherapies] = useGetPatientTherapies(patientProtocol.patientId)

  const sequences = useMemo(
    () => patientTherapies.filter(seq => seq.patientProtocolId === patientProtocol.id && !seq.isNote),
    [patientTherapies, patientProtocol.id]
  )
  const validatedCount = sequences.filter(isSequenceDoctorValidated).length
  const canRemove = validatedCount === 0
  const isStopped = Boolean(patientProtocol.endDate)
  const dateFormatter = getShortDateFormatter()

  function onKeep() {
    context.setOpen(false)
  }

  function onRemove() {
    setIsRemoving(true)
    void zero.attemptMutation(
      zero.mutators.patients.removeProtocol({patientProtocolId: patientProtocol.id, reason: reason || undefined}),
      async mutation => {
        const data = await mutation.server
        if (!data) {
          setIsRemoving(false)
          return
        }
        context.setOpen(false)
        toaster.success({title: t('success'), description: t('patient_updated_successfully')})
      }
    )
  }

  function onStopInstead() {
    context.setOpen(false)
    void dialog.open(`stop-patient-protocol-${patientProtocol.id}`, {
      children: <StopProtocolDialog patientProtocol={patientProtocol} />,
      contentProps: {minWidth: '28vw'},
    })
  }

  if (!canRemove) {
    return (
      <>
        <DialogHeader>
          <Text fontSize="lg" fontWeight="semibold">
            {t('protocol_cannot_be_removed', 'Protocol cannot be removed')}
          </Text>
        </DialogHeader>

        <DialogBody minHeight="none">
          <VStack align="stretch" gap="4" width="100%">
            <Text fontSize="sm" color="fg.muted">
              {isStopped
                ? t(
                    'stopped_protocol_with_validated_cannot_be_removed_description',
                    'A protocol with prescriptions validated by a doctor cannot be removed.'
                  )
                : t(
                    'protocol_with_validated_cannot_be_removed_description',
                    'A protocol with prescriptions validated by a doctor cannot be removed. Stop the protocol instead; its history stays visible.'
                  )}
            </Text>

            <Box bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border" p="4">
              <VStack align="stretch" gap="2">
                <HStack gap="2">
                  <Text fontSize="sm" color="fg.muted">
                    {t('protocol')}:
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {patientProtocol.protocol?.name ?? '-'}
                  </Text>
                </HStack>
                <HStack gap="2">
                  <Text fontSize="sm" color="fg.muted">
                    {t('validated_prescriptions', 'Validated prescriptions')}:
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" color="red.fg">
                    {validatedCount}
                  </Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onKeep}>
              {t('close')}
            </Button>
            {!isStopped && (
              <Button colorPalette="cyan" onClick={onStopInstead}>
                {t('stop_protocol_instead', 'Stop protocol instead')}
              </Button>
            )}
          </ButtonGroup>
        </DialogFooter>
      </>
    )
  }

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('remove_protocol_confirm', 'Remove protocol?')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight="none">
        <VStack align="stretch" gap="4" width="100%">
          <Text fontSize="sm" color="fg.muted">
            {t(
              'remove_protocol_not_validated_description',
              'The protocol and all its prescriptions are removed from the patient.'
            )}
          </Text>

          <Box bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border" p="4">
            <VStack align="stretch" gap="2">
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('protocol')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {patientProtocol.protocol?.name ?? '-'}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('added_on_label')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {patientProtocol.createdAt ? dateFormatter.format(patientProtocol.createdAt) : '-'}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('added_by', 'Added by')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {patientProtocol.created?.displayUsername ?? '-'}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('prescriptions', 'Prescriptions')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {sequences.length}
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="medium" marginBottom="2">
              {t('reason_optional', 'Reason (optional)')}
            </Text>
            <Textarea
              placeholder={t('recorded_in_patient_history', 'Recorded in the patient history')}
              value={reason}
              onChange={e => {
                setReason(e.target.value)
              }}
              rows={3}
            />
          </Box>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={onKeep} disabled={isRemoving}>
            {t('keep_protocol', 'Keep protocol')}
          </Button>
          <Button colorPalette="red" onClick={onRemove} loading={isRemoving}>
            {t('remove_protocol', 'Remove protocol')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
